import { isServer } from "solid-js/web";

import { pubSub } from "@/lib/PubSub";
import { checkIsHomePage } from "@/lib/pathUtils.ts";
import { routerStore } from "@/stores/routerStore";
import { PubSubEvents } from "@/types/PubSub";

interface DialogTask {
  id: string;
  execute: () => void;
  sort?: number;
  dialogType?: string; // 添加对话框类型标识
}

interface DialogOptions {
  id?: string;
  sort?: number;
  delayMs?: number;
  dialogType?: string; // 添加对话框类型选项
}

interface RegisterDialogParams {
  execute: () => void;
  options?: DialogOptions;
  dialogType?: string; // 添加对话框类型参数
}

interface PopupConfig {
  execute: () => void;
  countdown?: number;
  sort?: number;
  dialogType?: string; // 添加对话框类型支持
}

interface QueueStatus {
  queueLength: number;
  isExecuting: boolean;
  currentDialog: string | null;
  delayedCount: number;
}

class DialogCenter {
  private queue: DialogTask[] = [];
  private isHomePopped = false;
  private isExecuting = false;
  private currentDialog: DialogTask | null = null;
  private closeDialogResolve:
    | ((value: void | PromiseLike<void>) => void)
    | null = null;
  private delayedTimeouts: Map<string, NodeJS.Timeout> = new Map();

  constructor() {
    // 监听对话框关闭事件
    pubSub.subscribe(PubSubEvents.CloseDialog, (dialogType) => {
      this.onDialogClosed(dialogType);
    });
  }

  // ==================== 路由检查方法 ====================
  /**
   * 检查当前路径是否为根路径（/:lang 或 /:lang/） 使用现有的 checkIsHomePage 函数进行判断
   *
   * @returns {boolean} 是否为根路径
   */
  private isRootPath = (): boolean => {
    if (isServer) return false;

    // 使用现有的 checkIsHomePage 函数，它已经正确处理了语言前缀
    return checkIsHomePage();
  };

  /**
   * 检查是否允许在当前路径执行弹窗
   *
   * @returns {boolean} 是否允许执行
   */
  private canExecuteOnCurrentPath = (): boolean => {
    return this.isRootPath();
  };

  /**
   * 检查是否应该执行弹窗
   *
   * @returns {boolean} 是否应该执行弹窗
   */
  private shouldPopup = (): boolean => {
    /* prettier-ignore */
    const rootPathCount = routerStore.history.filter((path) => path === "/").length;

    // 确保刷新后第一次访问首页才执行
    if (routerStore.history.length === 0) {
      console.log("popup - 进入第一次进入首页");
      this.isHomePopped = true;
      return true;
    }

    // 刷新后(不一定在首页), 的第一次访问首页才执行
    if (!this.isHomePopped && rootPathCount === 1) {
      this.isHomePopped = true;
      console.log("popup - 刷新后(不一定在首页)");
      return true;
    }

    return false;
  };

  // ==================== 注册方法 ====================
  // 基础注册方法
  register = (task: DialogTask): void => {
    this.queue.push(task);
    this.queue.sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0));
  };

  // 注册对话框 (立即执行)
  registerDialog = (params: RegisterDialogParams): void => {
    const { execute, options, dialogType } = params;

    const id = `dialog-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const task: DialogTask = {
      id: options?.id || id,
      execute,
      sort: options?.sort || 0,
      dialogType: dialogType || options?.dialogType,
    };

    this.register(task);
  };

  // 注册延迟对话框 (访问页面 X 秒后显示)
  registerDialogWithDelay = (
    params: RegisterDialogParams & { delayMs: number },
  ): void => {
    const { execute, delayMs, options, dialogType } = params;

    if (delayMs <= 0) {
      this.registerDialog({ execute, options, dialogType });
      return;
    }

    const taskId =
      options?.id ||
      `dialog-delayed-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // 记录注册时的路径状态，用于延迟执行时的验证
    const wasOnHomePage = this.canExecuteOnCurrentPath();

    const timeoutId = setTimeout(() => {
      // 确保在客户端环境中
      if (isServer) return;

      // 检查当前路径是否允许执行弹窗
      // 对于延迟弹窗，我们允许在以下情况下执行：
      // 1. 注册时在首页，现在仍在首页
      // 2. 注册时在首页，现在不在首页但仍在根路径（用户可能只是切换了语言）
      const currentPathAllowed = this.canExecuteOnCurrentPath();
      const isStillOnRootPath = checkIsHomePage();

      if (!wasOnHomePage || (!currentPathAllowed && !isStillOnRootPath)) {
        console.log("🚫 Dialog execution blocked: not on root path");
        this.delayedTimeouts.delete(taskId);
        return;
      }

      // 对于延迟弹窗，直接执行而不检查 shouldPopup()
      // 因为延迟弹窗在注册时已经验证过路径，不应该再次被 shouldPopup() 阻止
      this.registerDialog({
        execute,
        dialogType,
        options: { ...options, id: taskId },
      });

      // 直接执行延迟弹窗，不检查 shouldPopup()
      this.executeQueueForDelayedPopup();
      this.delayedTimeouts.delete(taskId);
    }, delayMs);

    this.delayedTimeouts.set(taskId, timeoutId);
  };

  // 批量注册弹窗
  registerPopups = (popups: PopupConfig[]): void => {
    try {
      // 检查当前路径是否允许执行弹窗
      if (!this.canExecuteOnCurrentPath()) {
        console.log("🚫 Popup registration blocked: not on root path");
        return;
      }

      this.clearQueue();

      /* prettier-ignore */
      const immediatePopups = popups.filter((popup) => !popup?.countdown || popup?.countdown === 0);
      /* prettier-ignore */
      const delayedPopups = popups.filter((popup) => popup?.countdown && popup?.countdown > 0);

      // 立即注册 countdown = 0 的弹窗
      immediatePopups.forEach((popup, index) => {
        this.registerDialog({
          execute: popup.execute,
          dialogType: popup.dialogType,
          options: {
            id: `popup-immediate-${index}`,
            sort: popup?.sort,
          },
        });
      });

      // 延迟注册 countdown > 0 的弹窗
      delayedPopups.forEach((popup, index) => {
        this.registerDialogWithDelay({
          execute: popup.execute,
          delayMs: (popup?.countdown || 0) * 1000,
          dialogType: popup.dialogType,
          options: {
            id: `popup-delayed-${index}`,
            sort: popup?.sort,
          },
        });
      });

      // 立即执行队列（只包含立即显示的弹窗）
      if (immediatePopups.length > 0) {
        this.executeQueue();
      }
    } catch (error) {
      console.error("Error registering popups:", error);
    }
  };

  // ==================== 队列执行 ====================
  executeQueue = async (): Promise<void> => {
    if (this.isExecuting) return;

    if (!this.canExecuteOnCurrentPath() || !this.shouldPopup()) {
      console.log("🚫 Queue execution blocked: not on root path");
      return;
    }

    this.isExecuting = true;
    while (this.queue.length > 0) {
      const task = this.queue.shift();
      if (!task) continue;

      this.currentDialog = task;

      // 执行传入的函数
      task.execute();

      await this.waitForDialogClose();
      this.currentDialog = null;
    }
    this.isExecuting = false;
  };

  // 延迟弹窗专用执行方法，跳过 shouldPopup() 检查
  executeQueueForDelayedPopup = async (): Promise<void> => {
    if (this.isExecuting) return;

    // 只检查路径，不检查 shouldPopup()
    if (!this.canExecuteOnCurrentPath()) {
      console.log("🚫 Delayed popup execution blocked: not on root path");
      return;
    }

    this.isExecuting = true;
    while (this.queue.length > 0) {
      const task = this.queue.shift();
      if (!task) continue;

      this.currentDialog = task;

      // 执行传入的函数
      task.execute();

      await this.waitForDialogClose();
      this.currentDialog = null;
    }
    this.isExecuting = false;
  };

  // ==================== 私有方法 ====================
  private waitForDialogClose = (): Promise<void> => {
    return new Promise((resolve) => {
      this.closeDialogResolve = resolve;
    });
  };

  private onDialogClosed = (dialogType?: string): void => {
    if (this.currentDialog && this.closeDialogResolve) {
      // 只有在当前任务的对话框类型与关闭的对话框类型匹配时才执行关闭逻辑
      if (
        dialogType &&
        this.currentDialog.dialogType &&
        dialogType === this.currentDialog.dialogType
      ) {
        this.closeDialogResolve();
        this.closeDialogResolve = null;
      }
    }
  };

  // ==================== 状态管理 ====================
  getCurrentDialog = (): DialogTask | null => {
    return this.currentDialog;
  };

  // 根据对话框类型获取当前对话框信息
  getCurrentDialogByType = (dialogType: string): DialogTask | null => {
    if (this.currentDialog && this.currentDialog.dialogType === dialogType) {
      return this.currentDialog;
    }
    return null;
  };

  // 检查指定类型的对话框是否正在显示
  isDialogTypeActive = (dialogType: string): boolean => {
    return this.currentDialog?.dialogType === dialogType;
  };

  // 移除指定ID的弹窗任务
  removeDialogById = (id: string): boolean => {
    const initialLength = this.queue.length;
    this.queue = this.queue.filter((task) => task.id !== id);
    return this.queue.length < initialLength; // 返回是否成功移除
  };

  getQueueStatus = (): QueueStatus => {
    return {
      queueLength: this.queue.length,
      isExecuting: this.isExecuting,
      currentDialog: this.currentDialog?.id || null,
      delayedCount: this.delayedTimeouts.size,
    };
  };

  clearQueue = (): void => {
    this.queue = [];
    this.isExecuting = false;
    this.currentDialog = null;
    this.isHomePopped = false; // 重置首页弹窗状态，允许重新触发

    // 清理所有延迟定时器
    this.delayedTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.delayedTimeouts.clear();

    if (this.closeDialogResolve) {
      this.closeDialogResolve();
      this.closeDialogResolve = null;
    }
  };

  destroy = (): void => {
    pubSub.unsubscribe(PubSubEvents.CloseDialog, this.onDialogClosed);
    this.clearQueue();
  };
}

const DialogSettingCenter = new DialogCenter();
export default DialogSettingCenter;
